// The parts "New chat", "New group" and "Add people" share: the find box, a person's
// round face, a person to tick, the people picked, and the one line a list says
// instead of rows.
import type { ReactElement, ReactNode, RefObject } from 'react';
import { Circle, CircleCheck, Search, X } from 'lucide-react';
import { Spinner } from '../loading';
import type { PickPerson } from '../../services/messagesApi';
import { initials } from './messageText';
import { pickLine, type FindFailure } from './usePeopleFinder';

/** As long as any name; a pasted block of text is cut here. */
const FIND_MAX_CHARS = 80;

export const FIND_PROMPT = 'Find by name';

export function FindField({ value, onChange, busy, inputRef, disabled = false }: {
  value: string;
  onChange: (value: string) => void;
  /** A new list is on its way while one is on screen. */
  busy: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        aria-label={FIND_PROMPT}
        placeholder={FIND_PROMPT}
        // Behind hover, not printed: the screen stays short.
        title="Your team is listed. Type two letters or more to find anyone in the project."
        autoComplete="off"
        spellCheck={false}
        maxLength={FIND_MAX_CHARS}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
      />
      {busy && <Spinner size="xs" className="absolute right-3 top-1/2 -translate-y-1/2" />}
    </div>
  );
}

/** The round face of a person, as the conversation list draws one. */
export function Face({ name }: { name: string }): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700"
    >
      {initials(name)}
    </span>
  );
}

/** The small quiet label over the list: the team, or what a search found. */
export function ListHeading({ searching }: { searching: boolean }): ReactElement {
  return (
    <p className="px-3 pb-1 pt-1 text-xs font-medium text-gray-500">{searching ? 'Search results' : 'Your team'}</p>
  );
}

const TRY_AGAIN = 'font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline';

/** The one line in place of a list that did not come. */
export function FindFailureLine({ failure, off, onRetry }: { failure: FindFailure; off: string; onRetry: () => void }): ReactElement {
  if (failure === 'off') return <Quiet>{off}</Quiet>;
  return (
    <Quiet>
      {failure === 'unconfirmed' ? 'Your sign-in could not be confirmed.' : 'People could not be loaded.'}{' '}
      <button type="button" onClick={onRetry} className={TRY_AGAIN}>Try again</button>
    </Quiet>
  );
}

export function Quiet({ children }: { children: ReactNode }): ReactElement {
  return <p className="px-3 py-6 text-center text-sm text-gray-500">{children}</p>;
}

const TICK_ROW = 'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent';

/** One person to pick or unpick: the face, the name, the grey line, and a tick. */
export function TickRow({ person, on, disabled, title, onToggle }: {
  person: PickPerson;
  on: boolean;
  disabled: boolean;
  /** Said on hover: why a row cannot be picked (no room left). */
  title?: string;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      aria-label={person.name}
      onClick={onToggle}
      disabled={disabled}
      title={title}
      className={TICK_ROW}
    >
      <Face name={person.name} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{person.name}</span>
        <span className="block truncate text-xs text-gray-500">{pickLine(person)}</span>
      </span>
      {on
        ? <CircleCheck className="h-5 w-5 flex-none text-primary-600" aria-hidden="true" />
        : <Circle className="h-5 w-5 flex-none text-gray-300" aria-hidden="true" />}
    </button>
  );
}

/** The people picked so far, as chips with a way out. Nothing at all when nobody is picked. */
export function PickedChips({ picked, disabled, onUnpick }: {
  picked: PickPerson[];
  disabled: boolean;
  onUnpick: (person: PickPerson) => void;
}): ReactElement | null {
  if (picked.length === 0) return null;
  return (
    <div className="flex max-h-24 flex-wrap items-center gap-1.5 overflow-y-auto pt-0.5" aria-label="Picked people" role="group">
      <span className="pr-0.5 text-xs font-medium text-gray-500">{picked.length} picked</span>
      {picked.map((person) => (
        <span key={person.empCode} className="flex max-w-full items-center gap-1 rounded-full bg-primary-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary-700">
          <span className="truncate">{person.name}</span>
          <button
            type="button"
            onClick={() => onUnpick(person)}
            disabled={disabled}
            aria-label={`Remove ${person.name}`}
            className="flex h-5 w-5 flex-none items-center justify-center rounded-full text-primary-600 hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}
    </div>
  );
}
